import { bulkAssignCompliancesToUsers, useCompanyData } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { AddAssigneesStep } from './AddAssigneesStep';
import { ConfirmRemoveAssigneeStep } from './ConfirmRemoveAssigneeStep';
import { ManageAssigneesStep } from './ManageAssigneesStep';

type BulkManageAssigneesStep = 'manage' | 'add' | 'confirm-remove';

interface BulkManageAssigneesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFilings: TComplianceItem[];
  onSuccess: () => void;
}

export const BulkManageAssigneesModal = ({
  isOpen,
  onClose,
  selectedFilings,
  onSuccess,
}: BulkManageAssigneesModalProps) => {
  const { setNotification } = useNotification();
  const [currentStep, setCurrentStep] =
    useState<BulkManageAssigneesStep>('manage');
  const [assigneesToAdd, setAssigneesToAdd] = useState<string[]>([]);
  const [assigneeToRemove, setAssigneeToRemove] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [currentAssignees, setCurrentAssignees] = useState<
    {
      id: string;
      name: string;
      email: string;
    }[]
  >([]);
  const [originalFilings, setOriginalFilings] = useState<TComplianceItem[]>([]);
  const isSubmitting = useToggle(false);

  const companyIdForFetch =
    originalFilings[0]?.company?.id ?? selectedFilings[0]?.company?.id;
  const { data: company } = useCompanyData(companyIdForFetch);

  useEffect(() => {
    if (isOpen && selectedFilings.length > 0) {
      setOriginalFilings(selectedFilings);

      const initialAssignees = selectedFilings.reduce<
        { id: string; name: string; email: string }[]
      >((acc, filing) => {
        filing.assignees?.forEach((assignee) => {
          if (!acc.find((a) => a.id === assignee.id)) {
            acc.push(assignee);
          }
        });
        return acc;
      }, []);
      setCurrentAssignees(initialAssignees);
    }
  }, [isOpen]);

  const handleClose = () => {
    setCurrentStep('manage');
    setAssigneesToAdd([]);
    setAssigneeToRemove(null);
    setCurrentAssignees([]);
    setOriginalFilings([]);
    onClose();
  };

  const handleConfirmChanges = async () => {
    isSubmitting.on();

    try {
      const requestBody = {
        complianceItemIds: originalFilings.map((filing) => filing.id),
        assigneeIdsToAdd: assigneesToAdd,
        companyId: originalFilings[0]?.company?.id || 0,
      };

      const response = await bulkAssignCompliancesToUsers(requestBody);

      if (!response?.ok) {
        throw new Error('Failed to update assignees');
      }

      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Assignees updated successfully.',
      });

      const newAssignees = (company?.members || [])
        .filter((member) => assigneesToAdd.includes(member.id))
        .map((member) => ({
          id: member.id,
          name: member.name,
          email: member.email,
        }));
      setCurrentAssignees((prev) => [...prev, ...newAssignees]);

      setAssigneesToAdd([]);
      setAssigneeToRemove(null);

      onSuccess();
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update assignees. Please try again.',
      });
    } finally {
      isSubmitting.off();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'manage':
        return (
          <ManageAssigneesStep
            assignees={currentAssignees}
            onAddAssignees={() => setCurrentStep('add')}
            onRemoveAssignee={(assignee) => {
              setAssigneeToRemove(assignee);
              setCurrentStep('confirm-remove');
            }}
          />
        );
      case 'add':
        return (
          <AddAssigneesStep
            company={company}
            currentAssignees={currentAssignees}
            onBack={() => setCurrentStep('manage')}
            onAssigneesSelected={(selectedAssigneeIds) => {
              setAssigneesToAdd(selectedAssigneeIds);
            }}
            selectedAssignees={assigneesToAdd}
          />
        );
      case 'confirm-remove':
        return (
          <ConfirmRemoveAssigneeStep
            assigneeToRemove={assigneeToRemove}
            companyDisplayName={originalFilings[0]?.company?.companyDisplayName}
            companyCountryCode={
              originalFilings[0]?.corporation?.incorporationCountry
            }
          />
        );
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (currentStep === 'add') {
      return (
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => setCurrentStep('manage')}
            disabled={isSubmitting.visible}
          >
            Back
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await handleConfirmChanges();
              setCurrentStep('manage');
            }}
            disabled={assigneesToAdd.length === 0 || isSubmitting.visible}
          >
            {isSubmitting.visible ? (
              <div className="flex items-center gap-sm">
                <LoadingIndicator size={16} />
                Adding
              </div>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      );
    }

    if (currentStep === 'confirm-remove') {
      return (
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => setCurrentStep('manage')}
            disabled={isSubmitting.visible}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              if (assigneeToRemove) {
                isSubmitting.on();

                try {
                  const requestBody = {
                    complianceItemIds: originalFilings.map(
                      (filing) => filing.id
                    ),
                    assigneeIdsToRemove: [assigneeToRemove.id],
                    companyId: originalFilings[0]?.company?.id || 0,
                  };

                  const response =
                    await bulkAssignCompliancesToUsers(requestBody);

                  if (!response?.ok) {
                    throw new Error('Failed to remove assignee');
                  }

                  setNotification({
                    show: true,
                    valid: true,
                    title: 'Success',
                    message: 'Assignee removed successfully.',
                  });

                  setAssigneesToAdd([]);
                  setAssigneeToRemove(null);
                  onSuccess();
                  setCurrentAssignees((prev) =>
                    prev.filter(
                      (assignee) => assignee.id !== assigneeToRemove.id
                    )
                  );

                  setCurrentStep('manage');
                } catch (error) {
                  setNotification({
                    show: true,
                    valid: false,
                    title: 'Error',
                    message: 'Failed to remove assignee. Please try again.',
                  });
                } finally {
                  isSubmitting.off();
                }
              }
            }}
            disabled={isSubmitting.visible}
          >
            {isSubmitting.visible ? (
              <div className="flex items-center gap-sm">
                <LoadingIndicator size={16} />
                Removing
              </div>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      );
    }

    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[520px]">
        {currentStep !== 'confirm-remove' && (
          <DialogHeader className="flex flex-col gap-xs items-start">
            <DialogTitle>
              {currentStep === 'manage' ? 'Manage assignees' : 'Add assignees'}
            </DialogTitle>
            <DialogDescription>
              {currentStep === 'manage'
                ? 'You can add or remove assignees at any time.'
                : 'Select team members to assign to the selected filings.'}
            </DialogDescription>
          </DialogHeader>
        )}

        {renderStepContent()}

        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
};
